import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, KeyRound, Trash2, ToggleLeft, ToggleRight, Loader2, Pencil,
  Briefcase,
} from "lucide-react";
import { HeroKpi, AllocationBar } from "../components/HeroKpi";

function SectionBlock({ title, icon, children }) {
  return (
    <div className="rounded-md border border-[#181818] bg-[#080808] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-[#737373] flex items-center gap-1.5 mb-3">
        <span className="opacity-70">{icon}</span>{title}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {children}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, accent, testid }) {
  return (
    <div className="p-2.5 rounded bg-[#0a0a0a] border border-[#161616]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-[13px] mt-1 ${accent || "text-[#D4D4D4]"}`}>{value}</div>
    </div>
  );
}

export default function SociosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", capital_invested: 0, status: "ativo" });
  const [saving, setSaving] = useState(false);
  const [loginModal, setLoginModal] = useState(null);
  const [loginPwd, setLoginPwd] = useState("");
  const [loginSaving, setLoginSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // socio sendo editado
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/socios");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/socios", { ...form, capital_invested: Number(form.capital_invested) });
      toast.success("Sócio criado");
      setOpen(false);
      setForm({ name: "", email: "", whatsapp: "", capital_invested: 0, status: "ativo" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/socios/${s.id}`, { status: s.status === "ativo" ? "inativo" : "ativo" });
      load();
    } catch (_) { toast.error("Erro"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir sócio "${s.name}"?`)) return;
    try { await api.delete(`/socios/${s.id}`); toast.success("Excluído"); load(); }
    catch(_) { toast.error("Erro"); }
  };

  const createLogin = async (e) => {
    e.preventDefault();
    if (!loginModal) return;
    setLoginSaving(true);
    try {
      await api.post(`/socios/${loginModal.id}/login`, { password: loginPwd });
      toast.success("Login do sócio salvo");
      setLoginModal(null);
      setLoginPwd("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setLoginSaving(false); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      name: s.name || "",
      email: s.email || "",
      whatsapp: s.whatsapp || "",
      capital_invested: s.capital_invested ?? 0,
      status: s.status || "ativo",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editForm) return;
    if (!editForm.name.trim()) { toast.error("Nome obrigatório"); return; }
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) { toast.error("E-mail inválido"); return; }
    if (editForm.whatsapp && editForm.whatsapp.replace(/\D/g, "").length < 10) { toast.error("WhatsApp inválido"); return; }
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase() || null,
        whatsapp: editForm.whatsapp.trim(),
        capital_invested: Number(editForm.capital_invested) || 0,
        status: editForm.status,
      };
      await api.patch(`/socios/${editing.id}`, payload);
      toast.success("Sócio atualizado");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setEditSaving(false); }
  };

  // Agregado (todas as carteiras) — sempre exibido no topo
  const agg = items.reduce((a, s) => ({
    investido: a.investido + (s.capital_investido_total || s.capital_invested || 0),
    na_rua: a.na_rua + (s.capital_na_rua || 0),
    disponivel: a.disponivel + (s.capital_disponivel || 0),
    lucro_saque: a.lucro_saque + (s.lucro_disponivel_saque || 0),
  }), { investido: 0, na_rua: 0, disponivel: 0, lucro_saque: 0 });

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Capital</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Sócios</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Visão executiva consolidada da carteira de cada sócio.</p>
        </div>
        <button onClick={()=>setOpen(true)} className="fc-btn-gold flex items-center gap-2" data-testid="btn-new-socio">
          <Plus size={15}/> Novo sócio
        </button>
      </div>

      {/* HERO KPIs agregados — visão executiva da operação */}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-7" data-testid="socios-hero-aggregate">
          <HeroKpi label="Capital Investido Total" value={formatBRL(agg.investido)} variant="investido"/>
          <HeroKpi label="Capital na Rua" value={formatBRL(agg.na_rua)} variant="rua"/>
          <HeroKpi label="Capital Disponível" value={formatBRL(agg.disponivel)} variant="disponivel"
            valueColor={agg.disponivel < 0 ? "text-red-300" : undefined}/>
          <HeroKpi label="Lucro disponível p/ saque" value={formatBRL(agg.lucro_saque)} variant="lucro" dim={!(agg.lucro_saque > 0)}/>
        </div>
      )}

      {/* 🏆 Pódio — Top 3 sócios por Capital Investido Total */}
      {!loading && items.length > 0 && (() => {
        const podium = [...items]
          .filter(s => (s.capital_investido_total || s.capital_invested || 0) > 0)
          .sort((a, b) => (b.capital_investido_total || b.capital_invested || 0) - (a.capital_investido_total || a.capital_invested || 0))
          .slice(0, 3);
        if (podium.length === 0) return null;
        const medals = ["🥇", "🥈", "🥉"];
        const ringColors = ["border-[#D4AF37]/60", "border-[#A3A3A3]/40", "border-[#B45309]/40"];
        return (
          <div className="fc-card p-5 sm:p-6 mb-7" data-testid="socios-podium">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mb-4 flex items-center gap-2">
              🏆 Ranking — Maiores carteiras
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {podium.map((s, i) => (
                <div key={s.id} className={`p-4 rounded-md border ${ringColors[i]} bg-[#0a0a0a] relative overflow-hidden`} data-testid={`podium-${i+1}`}>
                  <div className="text-2xl mb-1">{medals[i]}</div>
                  <div className="font-display text-[15px] font-semibold truncate">{s.name}</div>
                  <div className="font-mono-tab text-[22px] text-[#E8C547] mt-1.5 font-bold">{formatBRL(s.capital_investido_total ?? s.capital_invested)}</div>
                  <div className="text-[11px] text-[#737373] mt-1">{s.contratos_ativos ?? 0} contratos · {(s.capital_na_rua_percent || 0).toFixed(1)}% alocado</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-4">
        {loading && [1,2].map(i=><div key={i} className="fc-card h-72 shimmer"/>)}
        {!loading && items.length === 0 && <div className="col-span-full text-center text-[#737373] py-16 fc-card">Nenhum sócio cadastrado.</div>}
        {!loading && items.map(s => {
          const disp = s.capital_disponivel || 0;
          const investido = s.capital_investido_total ?? s.capital_invested ?? 0;
          const naRua = s.capital_na_rua || 0;
          const pctAlocado = investido > 0 ? Math.min((naRua / investido) * 100, 100) : 0;
          const isOpen = !!expanded[s.id];
          return (
            <div key={s.id} className="fc-card p-5 fc-card-hover" data-testid={`socio-card-${s.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]"><Briefcase size={18}/></div>
                  <div>
                    <div className="font-display text-[17px] font-semibold tracking-tight">{s.name}</div>
                    <div className="text-[12px] text-[#A3A3A3]">{s.email || "—"}</div>
                  </div>
                </div>
                <button onClick={()=>toggle(s)} className="text-[#A3A3A3] hover:text-white">
                  {s.status === "ativo" ? <ToggleRight size={26} className="text-[#D4AF37]"/> : <ToggleLeft size={26}/>}
                </button>
              </div>

              {/* HERO KPIs do sócio — sempre visíveis */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <HeroKpi label="Capital Investido" value={formatBRL(investido)} variant="investido" testid={`socio-investido-${s.id}`}/>
                <HeroKpi label="Capital na Rua" value={formatBRL(naRua)} variant="rua" testid={`socio-exposicao-${s.id}`}/>
                <HeroKpi label="Capital Disponível" value={formatBRL(disp)} variant="disponivel"
                  valueColor={disp < 0 ? "text-red-300" : undefined} testid={`socio-disponivel-${s.id}`}/>
                <HeroKpi label="Lucro disponível p/ saque" value={formatBRL(s.lucro_disponivel_saque)} variant="lucro" dim={!(s.lucro_disponivel_saque > 0)} testid={`socio-lucro-saque-${s.id}`}/>
              </div>

              {/* Barra de progresso — Capital na Rua / Investido */}
              <div className="mb-4 px-1">
                <div className="flex items-center justify-between text-[10.5px] mb-1.5">
                  <span className="text-[#737373] uppercase tracking-[0.16em] font-medium">Alocação do capital</span>
                  <span className="font-mono-tab text-[#E8C547] font-semibold text-[13px]" data-testid={`socio-pct-${s.id}`}>{pctAlocado.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden border border-[#262626]">
                  <div className="h-full transition-all" style={{ width: `${pctAlocado}%`, background: "linear-gradient(90deg, #997A15 0%, #D4AF37 60%, #E8C547 100%)" }}/>
                </div>
                <div className="flex justify-between text-[10.5px] text-[#737373] mt-1">
                  <span>Na rua: <span className="text-white font-mono-tab">{formatBRL(naRua)}</span></span>
                  <span>Disponível: <span className={`font-mono-tab ${disp < 0 ? "text-red-300" : "text-emerald-300"}`}>{formatBRL(disp)}</span></span>
                </div>
              </div>

              {/* Detalhes — ocultos por padrão */}
              {isOpen && (
                <div className="space-y-3 mt-2 pt-4 border-t border-[#1a1a1a]">
                  <AllocationBar investido={investido} naRua={naRua} disponivel={disp} testid={`socio-allocation-${s.id}`}/>

                  <SectionBlock title="Resultado Financeiro" icon="📈">
                    <MiniKpi label="Juros recebidos" value={formatBRL(s.juros_recebidos)} accent="text-emerald-300"/>
                    <MiniKpi label="Lucros recebidos" value={formatBRL(s.total_lucros_recebidos)} accent="text-emerald-300"/>
                    <MiniKpi label="Lucros reinvestidos" value={formatBRL(s.total_lucros_reinvestidos)} accent="text-[#D4AF37]"/>
                  </SectionBlock>

                  <SectionBlock title="Movimentações" icon="💰">
                    <MiniKpi label="Total aportes" value={formatBRL(s.total_aportes)} accent="text-emerald-300"/>
                    <MiniKpi label="Total retiradas" value={formatBRL(s.total_retiradas)} accent="text-red-300"/>
                  </SectionBlock>

                  <SectionBlock title="Exposição" icon="📊">
                    <MiniKpi label="Contratos" value={formatBRL(s.capital_na_rua_contratos)} accent="text-[#D4AF37]"/>
                    <MiniKpi label="Parcelados/Acordos" value={formatBRL(s.capital_na_rua_parcelados)} accent="text-purple-300"/>
                    <MiniKpi label="Exposição total" value={formatBRL(s.capital_na_rua)} accent="text-[#D4AF37]"/>
                    <MiniKpi label="Juros a receber" value={formatBRL(s.juros_a_receber)} accent="text-yellow-300"/>
                  </SectionBlock>

                  <SectionBlock title="Saúde Financeira" icon="⚠️">
                    <MiniKpi label="Déficit" value={formatBRL(s.total_deficit)} accent={s.total_deficit > 0 ? "text-red-300" : ""}/>
                    <MiniKpi label="Recomposição déficit" value={formatBRL(s.total_recomposicao_deficit)} accent="text-blue-300"/>
                    <MiniKpi label="Saldo operacional" value={formatBRL(s.saldo_operacional)} accent={s.saldo_operacional < 0 ? "text-red-300" : "text-emerald-300"}/>
                    <MiniKpi label="Contratos ativos / quitados" value={`${s.contratos_ativos ?? 0} / ${s.contratos_quitados ?? 0}`}/>
                  </SectionBlock>
                </div>
              )}

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1f1f1f]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`badge-base ${s.status === "ativo" ? "badge-aprovado" : "bg-neutral-800 text-neutral-400"}`}>{s.status}</span>
                  {s.whatsapp && <span className="text-[11px] text-[#737373]">{s.whatsapp}</span>}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setExpanded(p => ({ ...p, [s.id]: !isOpen }))}
                    className="fc-btn-ghost text-[11px] py-1.5 px-3 flex items-center gap-1.5"
                    data-testid={`socio-toggle-${s.id}`}
                  >
                    {isOpen ? "Ocultar detalhes" : "Ver detalhes"}
                  </button>
                  <button onClick={()=>setLoginModal(s)} className="text-[#D4AF37]/80 hover:text-[#D4AF37] text-xs flex items-center gap-1" data-testid={`create-login-${s.id}`}>
                    <KeyRound size={13}/> Login
                  </button>
                  <button onClick={()=>openEdit(s)} className="text-[#A3A3A3] hover:text-white text-xs flex items-center gap-1" data-testid={`edit-socio-${s.id}`}>
                    <Pencil size={13}/> Editar
                  </button>
                  <button onClick={()=>remove(s)} className="text-red-400/70 hover:text-red-300 text-xs flex items-center gap-1">
                    <Trash2 size={13}/> Excluir
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setOpen(false)}>
          <form onSubmit={create} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="socio-form-modal">
            <h3 className="font-display text-2xl font-bold">Novo sócio</h3>
            <p className="text-[12.5px] text-[#A3A3A3] mt-1">Cada sócio possui carteira individual.</p>
            <div className="space-y-4 mt-5">
              <div><label className="fc-label">Nome</label><input required className="fc-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="socio-name"/></div>
              <div><label className="fc-label">E-mail</label><input type="email" className="fc-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="socio-email"/></div>
              <div><label className="fc-label">WhatsApp</label><input className="fc-input" value={form.whatsapp} onChange={e=>setForm({...form, whatsapp: e.target.value})}/></div>
              <div><label className="fc-label">Capital investido (R$)</label><input type="number" step="0.01" min="0" className="fc-input" value={form.capital_invested} onChange={e=>setForm({...form, capital_invested: e.target.value})} data-testid="socio-capital"/></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="socio-save">{saving && <Loader2 size={14} className="animate-spin"/>} Criar</button>
            </div>
          </form>
        </div>
      )}

      {loginModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setLoginModal(null)}>
          <form onSubmit={createLogin} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="socio-login-modal">
            <h3 className="font-display text-2xl font-bold">Login para {loginModal.name}</h3>
            <p className="text-[12.5px] text-[#A3A3A3] mt-2">{loginModal.email ? <>E-mail: <span className="text-white">{loginModal.email}</span></> : <span className="text-red-400">Cadastre o e-mail antes</span>}</p>
            <div className="mt-5">
              <label className="fc-label">Senha (mín. 6 caracteres)</label>
              <input type="password" minLength={6} required disabled={!loginModal.email} className="fc-input" value={loginPwd} onChange={e=>setLoginPwd(e.target.value)} data-testid="socio-login-password"/>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setLoginModal(null)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={!loginModal.email || loginSaving} className="fc-btn-gold flex items-center gap-2 disabled:opacity-50" data-testid="socio-login-save">{loginSaving && <Loader2 size={14} className="animate-spin"/>} Salvar</button>
            </div>
          </form>
        </div>
      )}

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>{setEditing(null); setEditForm(null);}}>
          <form onSubmit={saveEdit} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="socio-edit-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Editar</div>
            <h3 className="font-display text-2xl font-bold mt-1">{editing.name}</h3>
            <div className="space-y-4 mt-5">
              <div>
                <label className="fc-label">Nome</label>
                <input required className="fc-input" value={editForm.name} onChange={e=>setEditForm({...editForm, name: e.target.value})} data-testid="socio-edit-name"/>
              </div>
              <div>
                <label className="fc-label">E-mail</label>
                <input type="email" className="fc-input" value={editForm.email} onChange={e=>setEditForm({...editForm, email: e.target.value})} data-testid="socio-edit-email"/>
              </div>
              <div>
                <label className="fc-label">WhatsApp</label>
                <input className="fc-input" placeholder="(11) 99999-9999" value={editForm.whatsapp} onChange={e=>setEditForm({...editForm, whatsapp: e.target.value})} data-testid="socio-edit-whatsapp"/>
              </div>
              <div>
                <label className="fc-label">Capital investido (base) — R$</label>
                <input type="number" step="0.01" min="0" className="fc-input" value={editForm.capital_invested} onChange={e=>setEditForm({...editForm, capital_invested: e.target.value})} data-testid="socio-edit-capital"/>
                <p className="text-[11px] text-[#737373] mt-1">Aportes/retiradas continuam sendo lançados pela Tesouraria. Este é o capital base de referência.</p>
              </div>
              <div>
                <label className="fc-label">Status</label>
                <select className="fc-input" value={editForm.status} onChange={e=>setEditForm({...editForm, status: e.target.value})} data-testid="socio-edit-status">
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
              <div className="text-[11.5px] text-[#A3A3A3] bg-[#0a0a0a] border border-[#1f1f1f] rounded-md p-3">
                ℹ️ Contratos existentes do sócio <strong className="text-white">não são alterados</strong>. Apenas dados de cadastro e capital base são atualizados.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>{setEditing(null); setEditForm(null);}} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={editSaving} className="fc-btn-gold flex items-center gap-2" data-testid="socio-edit-save">{editSaving && <Loader2 size={14} className="animate-spin"/>} Salvar</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
